import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { callApi, HttpMethod } from "src/services/apiService";

interface BCIDDocument {
  id: number;
  entityId: string;
  documentType: string;
  blobPath: string;
  fileName: string;
  fileSize: number;
  contentType: string;
  uploadedDate: string;
  uploadedBy: number;
}

interface BCIDDocumentUploadProps {
  entityId: string | null;
  documentType: string;
  label: string;
  accept?: string;
  multiple?: boolean;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

const BCIDDocumentUpload: React.FC<BCIDDocumentUploadProps> = ({
  entityId,
  documentType,
  label,
  accept,
  multiple = true,
}) => {
  const [documents, setDocuments] = useState<BCIDDocument[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const loadDocuments = useCallback(async () => {
    if (!entityId) return;
    try {
      const response = await callApi(
        `bcid/enterprise/documents/${entityId}`,
        HttpMethod.GET,
      );
      const filtered = (response || []).filter(
        (d: BCIDDocument) => d.documentType === documentType,
      );
      setDocuments(filtered);
    } catch (err) {
      console.error("Error loading documents:", err);
    }
  }, [entityId, documentType]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !entityId) return;

    setError(null);
    setUploading(true);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadProgress(0);

        // Step 1: Get SAS upload URL
        const initiateResponse = await callApi(
          "bcid/enterprise/initiate-upload",
          HttpMethod.POST,
          {
            fileName: file.name,
            fileSize: file.size,
            contentType: file.type,
          },
        );

        if (!initiateResponse?.uploadUrl) {
          throw new Error("Failed to get upload URL");
        }

        // Step 2: Upload directly to Azure blob storage
        await axios({
          method: "put",
          url: initiateResponse.uploadUrl,
          data: file,
          headers: {
            "x-ms-blob-type": "BlockBlob",
            "Content-Type": file.type,
          },
          onUploadProgress: (progressEvent) => {
            const total = progressEvent.total || progressEvent.loaded;
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / total,
            );
            setUploadProgress(percentCompleted);
          },
        });

        // Step 3: Use blobPath from the initiate response
        const blobPath = initiateResponse.blobPath;

        // Step 4: Register document metadata
        await callApi("bcid/enterprise/documents", HttpMethod.POST, {
          entityId,
          documentType,
          blobPath,
          fileName: file.name,
          fileSize: file.size,
          contentType: file.type,
        });
      }

      // Reload document list
      await loadDocuments();
    } catch (err) {
      console.error("Upload error:", err);
      setError("Failed to upload file. Please try again.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Reset the file input
      e.target.value = "";
    }
  };

  const handleDelete = async (documentId: number) => {
    try {
      await callApi(
        `bcid/enterprise/documents/${documentId}`,
        HttpMethod.DELETE,
      );
      await loadDocuments();
    } catch (err) {
      console.error("Delete error:", err);
      setError("Failed to delete file. Please try again.");
    }
  };

  return (
    <div className="mb-3">
      <label className="form-label fw-bold" style={{ wordBreak: "break-word" }}>
        {label}
      </label>

      {/* File input */}
      <div className="mb-2">
        <input
          type="file"
          id={`bcid-doc-${documentType}`}
          style={{ display: "none" }}
          onChange={handleFileSelect}
          accept={accept}
          multiple={multiple}
          disabled={uploading || !entityId}
        />
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={() =>
            document.getElementById(`bcid-doc-${documentType}`)?.click()
          }
          disabled={uploading || !entityId}
        >
          <i className="bi bi-upload me-1"></i>
          Choose Files
        </button>
      </div>

      {/* Upload progress */}
      {uploading && (
        <div className="progress mb-2" style={{ height: "20px" }}>
          <div
            className="progress-bar progress-bar-striped progress-bar-animated"
            role="progressbar"
            style={{ width: `${uploadProgress}%` }}
          >
            {uploadProgress}%
          </div>
        </div>
      )}

      {/* Error message */}
      {error && <div className="alert alert-danger py-1 mb-2">{error}</div>}

      {/* No entity warning */}
      {!entityId && (
        <small className="text-muted">
          Save the enterprise first before uploading documents.
        </small>
      )}

      {/* Document list */}
      {documents.length > 0 && (
        <div className="list-group">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-2 bg-dark text-light border-secondary"
            >
              <div>
                <i className="bi bi-file-earmark me-2"></i>
                <span>{doc.fileName}</span>
                <small className="text-muted ms-2">
                  ({formatFileSize(doc.fileSize)})
                </small>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => handleDelete(doc.id)}
                title="Delete"
              >
                <i className="bi bi-trash"></i>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BCIDDocumentUpload;
